import mqtt from 'mqtt'
import config from '@/config'
import SystemConnection, { CONNECTION_STATUS } from '@/utils/system/SystemConnection'
import { isFeatureEnabled } from '@ahaslides-product/frontend-toolkit'
import logger from '@/logger'
import TopicManager from './TopicManager'

const logFn = logger.extend('mqtt')

const connectOptions = {
  protocolVersion: 5,
  keepalive: 25,
}

class MqttClient {
  constructor(url, options) {
    this.client = mqtt.connect(url, options)
    this.listenConnectionEvents()
  }

  getClient() {
    return this.client
  }

  listenConnectionEvents() {
    this.client.on('connect', () => {
      logFn('Connected to MQTT broker')
      SystemConnection.detectEmqxConnection(CONNECTION_STATUS.GOOD)
    })

    this.client.on('reconnect', () => {
      logFn('Reconnecting to MQTT broker')
      Object.keys(this.client._resubscribeTopics).forEach((topic) => {
        this.client.subscribe(topic)
      })

      if (!Object.keys(this.client._resubscribeTopics).length) {
        TopicManager.getSubscribedTopics().forEach((topic) => {
          this.client.subscribe(topic)
        })
      }
    })

    this.client.on('close', () => {
      logFn('Connection to MQTT broker closed')
      SystemConnection.detectEmqxConnection(CONNECTION_STATUS.ERROR)
    })

    this.client.on('error', (err) => {
      logFn('Connection to MQTT broker error', err)
      SystemConnection.detectEmqxConnection(CONNECTION_STATUS.ERROR)
    })

    this.client.on('offline', () => {
      logFn('Connection to MQTT broker offline')
      SystemConnection.detectEmqxConnection(CONNECTION_STATUS.ERROR)
    })
  }

  subscribe(topic) {
    this.client.subscribe(topic)
    TopicManager.addTopic(topic)
  }

  unsubscribe(topic) {
    this.client.unsubscribe(topic)
    TopicManager.removeTopic(topic)
  }
}

const mqttClient = new MqttClient(config.mqttBrokerUrl, connectOptions)
const mqttClientInstance = mqttClient.getClient()

const getMessageHandlerWrapper = (handler) => {
  return (topic, message, packet) => {
    if (!packet.retain || isFeatureEnabled('release-mqtt-retain-message')) {
      handler(topic, message, packet)
    }
  }
}

// Use Proxy to intercept subscribe/unsubscribe calls and route them to MqttClient instance
// while forwarding all other calls to the underlying mqtt client
const proxiedClient = new Proxy(mqttClientInstance, {
  get(target, prop) {
    // Intercept subscribe and unsubscribe to use MqttClient methods
    if (prop === 'subscribe') {
      return (...args) => mqttClient.subscribe(...args)
    }
    if (prop === 'unsubscribe') {
      return (...args) => mqttClient.unsubscribe(...args)
    }
    if (prop === 'on') {
      return (...args) => {
        const [eventName, ...otherArgs] = args
        if (eventName === 'message') {
          const [handler] = otherArgs
          return target[prop](eventName, getMessageHandlerWrapper(handler))
        }

        return target[prop](...args)
      }
    }
    // Forward all other properties/methods to the underlying mqtt client
    return target[prop]
  },
})

export default proxiedClient
